export type PerformanceTimingEntry = {
  name: string;
  durationMs: number;
  startedAt: number;
  finishedAt: number;
  detail?: Record<string, string | number | boolean | null>;
};

const MAX_ENTRIES = 80;

function now() {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function publish(entry: PerformanceTimingEntry) {
  if (typeof window === 'undefined') return;
  const entries = (window.__SOLARGABAY_PERFORMANCE__ ??= []);
  entries.push(entry);
  if (entries.length > MAX_ENTRIES) entries.splice(0, entries.length - MAX_ENTRIES);
  window.dispatchEvent(new CustomEvent('solargabay:performance', { detail: entry }));
}

export function startTiming(
  name: string,
  detail?: PerformanceTimingEntry['detail'],
): () => PerformanceTimingEntry {
  const startedAt = now();
  const markStart = `solargabay:${name}:start:${startedAt}`;
  if (typeof performance !== 'undefined' && 'mark' in performance) {
    performance.mark(markStart);
  }

  return () => {
    const finishedAt = now();
    const entry: PerformanceTimingEntry = {
      name,
      startedAt,
      finishedAt,
      durationMs: Math.max(0, finishedAt - startedAt),
      detail,
    };
    if (typeof performance !== 'undefined' && 'mark' in performance && 'measure' in performance) {
      const markEnd = `solargabay:${name}:end:${finishedAt}`;
      performance.mark(markEnd);
      try {
        performance.measure(`solargabay:${name}`, markStart, markEnd);
      } catch {
        // A browser may evict marks under memory pressure; the local entry still records the timing.
      }
      if ('clearMarks' in performance && typeof performance.clearMarks === 'function') {
        performance.clearMarks(markStart);
        performance.clearMarks(markEnd);
      }
    }
    publish(entry);
    return entry;
  };
}

export async function withTiming<T>(
  name: string,
  task: () => Promise<T> | T,
  detail?: PerformanceTimingEntry['detail'],
): Promise<T> {
  const finish = startTiming(name, detail);
  try {
    return await task();
  } finally {
    finish();
  }
}

export function timedImport<T>(name: string, importer: () => Promise<T>): Promise<T> {
  return withTiming(`lazy-import:${name}`, importer, { routeChunk: true });
}
